// =============================================================================
// site/src/config/negocio.ts — FONTE ÚNICA DE CONTEÚDO DO SITE
// Espelha data/site-conteudo.md (Sprint A1). Nenhum componente pode escrever
// telefone, endereço, preço ou número de qualquer natureza fora daqui.
//
// REGRA DE OURO (WEB_PLAN §1.2 C2): `null` = dado ainda não confirmado.
// Nada de placeholder plausível — placeholder plausível vaza para produção e
// vira publicidade enganosa (CDC). Campo null some da página e some do JSON-LD.
//
// Enquanto houver pendência OBRIGATÓRIA, o site inteiro sai com noindex e o
// `npm run build:prod` falha. Ver scripts/check-conteudo.ts.
// =============================================================================

export type Talvez<T> = T | null;

export interface Horario {
  /** 0=domingo … 6=sábado (mesma numeração de Date.getDay) */
  dias: number[];
  abre: string;  // 'HH:MM'
  fecha: string; // 'HH:MM'
}

export interface Produto {
  nome: string;
  marca: Talvez<string>;
  /** null => "Consultar preço" (nunca inventar preço) */
  preco: Talvez<number>;
  descricao: Talvez<string>;
  /** A foto recortada, em /produtos (11/09/2026). NULA enquanto não houver a
   *  foto DAQUELE produto — a mesma régua do preço: cartão sem foto é honesto,
   *  cartão com a foto de outra peça é propaganda enganosa. Gerada por
   *  `node scripts/fotos-vitrine.mjs`, que tira o fundo branco do estúdio (o
   *  site é #111111) e padroniza tudo em 480x480. */
  foto: Talvez<{ src: string; alt: string }>;
}

export const negocio = {
  // --- 0. Marca (Manual de Identidade v1.0/2026 — não é dado a coletar) ------
  /** Marca principal. "Autopeças" é descritivo comercial, usado onde ajuda a
      localizar o negócio no segmento (SEO local, NAP) — nunca preso à marca. */
  marca: 'SERGINHO',
  slogan: 'Sonhe grande, sinta o movimento.',
  /** Manifesto do manual — copy institucional aprovada, usada em /sobre. */
  manifesto: [
    'SERGINHO existe para mover pessoas, negócios e oportunidades.',
    'Nossa trajetória começou no fornecimento de peças, mas a visão vai além: construir uma empresa reconhecida pela confiança, pela qualidade e pela inovação.',
    'Cada peça entregue, cada cliente atendido e cada evolução da empresa reforça o mesmo compromisso.',
  ],

  // --- 1. Identificação ------------------------------------------------------
  nome: 'Serginho Autopeças',
  /* RAZÃO SOCIAL x NOME FANTASIA — os dois são diferentes aqui, e de propósito.
     A loja é firma individual ("Empresário (Individual)" no CNPJ), então a razão
     social é o nome civil do titular; o nome de fachada é Serginho Autopeças, e
     é ele que está em `nome`. A política de privacidade usa os dois na mesma
     frase ("<razão social>, CNPJ <...>, atuando sob o nome <nome>"), que é a
     forma correta de identificar o controlador na LGPD.

     Este campo dizia 'ME' de 2026-08-06 a 2026-09-11. "ME" é o PORTE da empresa
     (Micro Empresa), uma linha do cartão CNPJ — não o nome dela. O erro saía em
     "© 2026 ME" no rodapé de toda página e em "legalName":"ME" no dado
     estruturado que o Google lê, e passou por uma confirmação do dono em 06/08
     que provavelmente respondeu "o porte está certo" a uma pergunta sobre o
     nome. Corrigido em 11/09 contra o registro público da Receita.

     Acentuação: é "Sergio", SEM acento — confirmado pelo dono em 11/09/2026,
     com a pergunta feita sobre o acento em si, não sobre "o valor está certo".
     A Receita grafa "SERGIO PRIETO GARCIA", mas isso não servia de prova: ela
     normaliza tudo para caixa alta sem diacríticos. Por um dia este campo teve
     "Sérgio", que era a grafia usual em português e era chute. */
  razaoSocial: 'Sergio Prieto Garcia' as Talvez<string>,
  cnpj: '03.663.583/0001-29' as Talvez<string>,
  /** Ano exato de abertura (28/04/1997, confirmado pelo dono em 2026-08-05).
      Necessário para foundingDate no JSON-LD.

      NÃO "CORRIJA" PARA 2000. O CNPJ 03.663.583/0001-29 consta na Receita com
      início de atividade em 25/02/2000, e quem compara os dois números acha
      que o site infla a idade da loja. Não infla: a loja abriu em 1997 e foi
      registrada de novo, sob novo CNPJ, em 2000 — mesma loja, mesmo dono. O dono
      confirmou isso em 11/09/2026, quando a divergência foi levantada.
      (Não escreva "mesmo balcão", "mesmo endereço" nem "esquina": a loja não
      fica numa esquina e mudou de endereço ao longo dos anos — o dono, 25/09/2026.) É 1997 que vai no site, no JSON-LD e no Google Business. */
  anoFundacao: 1997 as Talvez<number>,
  /** Derivado de anoFundacao, não digitado: 1997 → 2026 são 29 anos.
      A regra do site-conteudo.md §1 é explícita — quando o ano exato diverge
      da copy, o ano manda e a copy se corrige (era 25). */
  anosDeMercado: 29,

  // --- 2. Contato ------------------------------------------------------------
  /** E.164, ex.: '+555399998888'. DDD de Rio Grande/RS é 53.
      3231-1580 é o CELULAR do WhatsApp Business; 3204-3754 é a linha fixa
      (o dono, 16/10/2026 — corrige a nota de 06/08 que chamava o 3231 de
      fixo). Os dois são reais: o fixo recebe as ligações do Perfil do Google;
      o celular, as conversas. */
  whatsapp: '+555332311580' as Talvez<string>,
  telefoneFixo: '+555332043754' as Talvez<string>,
  email: 'serginhoautopecasrg@gmail.com',
  /* Os perfis se chamavam "Serginho JS" (@js.serginho) até 08/10/2026, quando
     o dono os renomeou para o nome da fachada — e o ENDEREÇO mudou junto, ao
     contrário do que este comentário supunha: links passados por ele em
     08/10/2026. No Facebook vai o endereço COM NÚMERO, e não o curto
     (facebook.com/serginho.autopecasrg): conferido em 08/10/2026 sem login, o
     curto mostra "conteúdo para maiores de 18, entre para ver", e o com número
     mostra a página da loja. O número também não muda se o nome mudar de novo. */
  instagram: 'https://www.instagram.com/serginho.autopecas/' as Talvez<string>,
  facebook: 'https://www.facebook.com/people/Serginho-Autope%C3%A7as/100064087951508/' as Talvez<string>,

  // --- 3. Endereço e horários ------------------------------------------------
  cidade: 'Rio Grande',
  uf: 'RS',
  endereco: {
    rua: 'Cristóvão Colombo' as Talvez<string>,
    numero: '427' as Talvez<string>,
    bairro: 'Cidade Nova' as Talvez<string>,
    cep: '96211-510' as Talvez<string>,
  },
  geo: {
    lat: -32.03846078973405 as Talvez<number>,
    lng: -52.110054262649854 as Talvez<number>,
  },
  /** Link curto do Google Maps (aba Compartilhar). */
  mapsUrl: 'https://maps.app.goo.gl/B25bNtBQQW9Rf39fA' as Talvez<string>,
  /** Seg-sex fecha para o almoço, então são duas faixas no mesmo dia.
      Sábado é faixa própria. Domingo fechado = não listado (§3).

      SÁBADO PUBLICA 12:15 — o mesmo horário em que a porta fecha (decisão do
      dono, 11/09/2026, revendo a de 10/09; manual §6.1).

      A versão anterior publicava 12:00 e fechava 12:15, para dar margem ao
      último cliente. O dono preferiu publicar o horário real: quem procura
      autopeça no sábado de manhã decide pelo que lê, e quinze minutos a menos
      no anúncio custam atendimento de quem chegaria dentro do horário.

      Este campo alimenta o site, o rodapé, o FAQ e o `openingHoursSpecification`
      do JSON-LD, que é o que o Google e os diretórios leem. */
  horarios: [
    { dias: [1, 2, 3, 4, 5], abre: '08:30', fecha: '12:00' },
    { dias: [1, 2, 3, 4, 5], abre: '13:30', fecha: '18:30' },
    { dias: [6],             abre: '08:30', fecha: '12:15' },
  ] as Horario[],

  // --- 4. Posicionamento (decisão D2) ---------------------------------------
  /** 'carro' | 'moto' | 'ambos' — define a copy inteira do site.
      Decisão D2 fechada: só automóveis (§4). */
  linhas: 'carro' as Talvez<'carro' | 'moto' | 'ambos'>,
  marcas: [
    'Mahle (Metal Leve)', 'KS (Kolbenschmidt)', 'Sabó', 'Continental', 'Dayco',
    'Gates', 'Taranto', 'Mann-Filter', 'Tecfil', 'Fram', 'Delphi',
    'Magneti Marelli', 'Wahler', 'Takao', 'Fras-le', 'Cobreq', 'Brembo', 'TRW',
    'Fremax', 'Bosch', 'Jurid', 'Hipper Freios', 'Nakata', 'Cofap', 'Monroe',
    'Axios (Monroe)', 'Viemar', 'Perfect', 'Sampel', 'Lemförder', 'LUK',
    'Sachs', 'Valeo', 'Spicer', 'Dana', 'Devigili', 'NGK (NTK)', 'Gauss',
    'Euro', 'Olympus', 'Marflex', 'Arteb', 'CIBIÉ', 'Orgus', 'Depo', 'TYC',
    'Fitam', 'Mobil', 'Shell', 'Castrol', 'Lubrax', 'Petronas (Selènia)',
    'Havoline', 'Motul', 'Radiex', 'Tirreno', 'WD-40', 'Koube',
  ] as string[],
  categorias: [
    'Motor e Componentes Internos',
    'Sistema de Freios',
    'Suspensão e Direção',
    'Transmissão e Embreagem',
    'Sistema Elétrico e Eletrônico',
    'Arrefecimento e Climatização',
    'Alimentação e Exaustão',
    'Iluminação e Sinalização',
    'Carroceria e Acabamento',
    'Fluidos, Lubrificantes e Químicos',
  ] as string[],
  diferenciais: [
    'Profissionais capacitados',
    'Entrega rápida',
    'Melhores preços',
    'Troca de lâmpadas e palhetas, colocação de calhas e calotas — cortesia do balcão',
  ] as string[],
  entrega: {
    /* "NA MAIORIA DOS BAIRROS", e não "todos" (14/09/2026, o dono). Dizia
       "todos os bairros" desde agosto, e não era verdade: quem entrega é a
       própria equipe, no meio do expediente, e o alcance depende de quem está
       na rua naquele dia. Prometer "todos" faz o cliente esperar por uma
       entrega que não vai sair. Ver /entrega-em-rio-grande, que explica isso em
       voz alta — e que, desde 15/09/2026, não cita NENHUM bairro como exemplo
       de "longe", nem no texto nem em comentário. */
    bairros: 'Rio Grande — na maioria dos bairros',
    /* PRAZO É null DESDE 14/09/2026, e isso é a regra 5 aplicada. O site dizia
       "15 minutos em média" desde agosto; na descrição do próprio dono a
       entrega sai no meio do expediente, com quem estiver na loja, e o alcance
       muda conforme o dia. Um número médio publicado vira a promessa pela qual
       o cliente cobra — e a /entrega-em-rio-grande já diz, em voz alta, que o
       horário é COMBINADO no pedido. Volta a ter valor quando o dono disser
       qual é o praticado; até lá a home não exibe prazo nenhum (index.astro
       guarda o null).

       A TAXA segue fora daqui de propósito: varia por bairro e muda com o
       combustível (o dono, 15/09/2026). O que o site publica é a referência do
       Centro (~R$ 8) dentro da FAQ, com a ressalva — tabela fixa envelheceria
       no primeiro reajuste. */
    prazo: null as Talvez<string>,
  } as Talvez<{ bairros: string; prazo: Talvez<string> }>,
  /** Parcelamento: o dono parcela CONFORME O VALOR da compra, sem regra fixa
      (16/10/2026: "500 em 10x sem juros, sim; 30 em 10x, não; a parcela tem
      que caber no bolso do cliente, e a taxa da maquininha pesa"). Por isso o
      site não promete "10x sem juros" — a checagem de marca (mkt.regra_casa,
      pagamento_proibido) acusa a promessa, que o Perfil do Google já tirou. */
  pagamentos: [
    'Cartão (parcelado conforme o valor)',
    'PIX',
    'Dinheiro',
  ] as string[],

  /** Prazo de cortesia para troca de peça comprada no BALCÃO por engano de
      aplicação — decisão do dono em 2026-09-11.

      Não é obrigação legal: na compra presencial o produto foi visto e
      conferido antes de pagar, e o direito de arrependimento do art. 49 do CDC
      vale para compra a distância. É cortesia da loja, e por isso o número tem
      de ser o PRATICADO no balcão: política publicada que a loja não cumpre é
      pior que não ter política.

      Até 11/09/2026 este prazo não existia como dado, e a página trazia no
      lugar uma nota interna — "Definir com o Serginho o prazo real (ex.: 7
      dias...)" — que o cliente lia. O marcador <Pendente> ao lado dela some em
      produção; o texto solto não somia. */
  trocaBalcaoDias: 7 as Talvez<number>,

  // --- 5. História (/sobre — E-E-A-T) ---------------------------------------
  /* QUEM FAZ A LOJA (22/09/2026, Fase 3). Os fatos abaixo saíram da entrevista
     do Serginho no podcast Fala Ernani (Rádio Zona Sul, ver `videoEntrevista`)
     e foram passados pelo filho, Luiz Felipe. Vão para a /sobre em prosa e para
     o cartão da loja no JSON-LD (founder, alternateName, award). Nada aqui é
     opinião do site: é o que o dono contou, com a fonte linkada na página.

     O NOME DO FUNDADOR É O DA RAZÃO SOCIAL — "Sergio" SEM acento —, e isso é
     deliberado: o dono confirmou a grafia em 11/09/2026 respondendo a uma
     pergunta sobre o acento em si (ver `razaoSocial`). O pedido da Fase 3 veio
     escrito "Sérgio"; o dono decidiu em 25/09/2026: "Sergio", sem acento.
     Um nome, uma grafia, nos dois lugares. */
  fundador: 'Sergio Prieto Garcia' as Talvez<string>,
  /** Como a loja se chamava de 1997 até a mudança de nome: mesma loja, mesmo
      balcão, mesmo dono. Vai em `alternateName` — é o nome pelo qual muita
      gente ainda procura. */
  nomeAnterior: 'JS Auto Peças' as Talvez<string>,
  /** A data completa da abertura (o `anoFundacao` acima é o ano). O JSON-LD
      passa a levar a data inteira em `foundingDate`. */
  dataFundacao: '1997-04-28' as Talvez<string>,
  /** Nome EXATO do prêmio, um item por ano, como o schema `award` pede. */
  premios: [
    'Prêmio Destaques do Ano Rio Grande 2023',
    'Prêmio Destaques do Ano Rio Grande 2024',
    'Prêmio Destaques do Ano Rio Grande 2025',
  ] as string[],
  premioUrl: 'https://www.instagram.com/premiodestaquesdoanoriogrande/' as Talvez<string>,
  /** A entrevista do Serginho. `publicadoEm`, `duracaoSegundos`, `titulo` e
      `canal` foram LIDOS da própria página do YouTube em 22/09/2026 (o vídeo é
      a gravação de uma transmissão ao vivo) — não são digitados de memória. O
      site só LINKA o vídeo: incorporar o player carregaria o Google antes do
      consentimento, o que a política de privacidade promete não fazer. */
  videoEntrevista: {
    url: 'https://www.youtube.com/watch?v=H16SQjPO_xw',
    id: 'H16SQjPO_xw',
    titulo: 'PODCAST FALA ERNANI',
    canal: 'Radio Zona Sul',
    publicadoEm: '2026-09-04',
    duracaoSegundos: 3788,
  } as Talvez<{ url: string; id: string; titulo: string; canal: string; publicadoEm: string; duracaoSegundos: number }>,
  /** A loja de MOTOPEÇAS ao lado, dos irmãos do Serginho. A Serginho é só de
      carro (D2), e quem procura peça de moto precisa saber para onde ir. */
  motoPecas: {
    nome: 'V&S Moto Peças',
    endereco: 'Cristóvão Colombo, 417',
    donos: 'Vera e Sandro',
  } as Talvez<{ nome: string; endereco: string; donos: string }>,
  /** O cumprimento da casa — uma linha na /sobre. Só o da casa: o da loja de
      motopeças ao lado não vai para o site (o dono, 25/09/2026).
      "bixão" com X, a mesma grafia do balão do mascote na home (decisão do
      dono em 25/09/2026; o pedido da Fase 3 veio "bichão"). */
  cumprimento: 'Fala bixão' as Talvez<string>,
  /** A equipe do BALCÃO. Os três atendem e conferem a peça; a entrega,
      normalmente, é do Alexander (o Alex) — o dono, 25/09/2026, corrigindo o
      "todos entregam" que a /sobre dizia. Cargo formal só entra confirmado um a um. */
  equipeBalcao: ['Diego dos Santos', 'Renan Constela', 'Alexander Monteiro'] as string[],
  /** Quem normalmente faz a entrega (o dono, 25/09/2026). */
  entregaNormalmente: 'Alexander' as Talvez<string>,
  /** O filho, e o que ele faz na loja — o Serginho chama disso a maior
      realização dele. "Luiz" com Z, como em `equipe` acima e no resto do site;
      o pedido da Fase 3 veio "Luís", e o dono decidiu "Luiz" em 25/09/2026.
      SEM FORMAÇÃO na página: ele pediu em 25/09/2026 que não fosse escrita (e a
      que tinha ido ao ar estava errada — é engenharia de automação, não de
      computação). Não recoloque. */
  sucessor: { nome: 'Luiz Felipe' } as Talvez<{ nome: string }>,
  /** Os carros do dono — a única coisa "fora do balcão" que entra na página,
      porque liga com a linha de antigos. */
  carrosDoDono: {
    primeiro: 'Fiat 147 1978, bege, apelidado de "Pudinzinho"',
    hoje: 'Variant 1972',
  } as Talvez<{ primeiro: string; hoje: string }>,
  historia: [
    'Dia 28 de abril de 1997, a loja abre. Desde lá a Serginho entrega as melhores peças com velocidade, preço justo e um atendimento de qualidade.',
    'Pode vir também para bater um papo e tomar um cafezinho.',
  ] as string[],

  /** As fotos da página /sobre. NULO ENQUANTO NÃO EXISTIREM — e é isso que a
   *  página lê para decidir o layout (10/09/2026).
   *
   *  Até aqui a /sobre desenhava uma moldura listrada de 4:3 escrito
   *  "[ Foto de arquivo · 1997 ]" — metade da tela de cada capítulo ocupada por
   *  um buraco anunciando que falta conteúdo. É a mesma regra 5 que proíbe
   *  texto plausível: espaço reservado para foto que não existe é o texto
   *  plausível em forma de layout.
   *
   *  Com nulo, o capítulo vira coluna única e respira. Basta pôr o arquivo em
   *  site/public/ e o caminho aqui para a página voltar a duas colunas —
   *  nenhuma outra mudança é necessária. `alt` é obrigatório junto: foto sem
   *  descrição é foto que o leitor de tela não vê. */
  fotos: {
    /* ADIADA pelo dono em 14/09/2026: as fotos antigas da história não entram
       nesta rodada. Fica `null` de propósito — e de propósito NÃO consta mais
       da tabela de pendências lá embaixo, porque é escopo e não dado faltando.
       O capítulo 01 já sabe viver sem ela: vira coluna única. */
    arquivo1997: null as Talvez<{ src: string; alt: string }>,
    /* AS FOTOS REAIS DA LOJA (11/09/2026, tiradas pelo dono). Só o ALT mora
       aqui — é conteúdo, e conteúdo é deste arquivo (regra 5). O ARQUIVO é
       importado na página por `astro:assets` (src/assets/fotos/*.jpg), que é
       quem gera AVIF/WebP e as larguras certas; este módulo também é lido pelo
       astro.config.mjs em Node, onde uma importação de imagem não existe.
       `null` tira a foto da página sem tocar em mais nada. */
    fachada:  { alt: 'Fachada da Serginho Autopeças na Rua Cristóvão Colombo, 427, Cidade Nova, Rio Grande' } as Talvez<{ alt: string }>,
    corredor: { alt: 'Corredor do estoque da Serginho Autopeças, com prateleiras de metal organizadas por código' } as Talvez<{ alt: string }>,
    /* A CALOTA SAIU DA /sobre EM 15/09/2026, a pedido do dono: "é meio feia e
       está num tamanho bem maior" que a fachada. Ele tinha razão, e dava para
       medir — a 1280×800 o quadrado ocupava 254 mil px² contra 148 mil da
       fachada, 72% a mais. A foto que menos diz sobre a loja era a maior da
       página, e a cara da loja era a menor.

       Fica `null`, não apagada: o arquivo continua em src/assets/fotos e o
       `alt` continua escrito, para o dia em que houver um lugar em que uma
       parede de calotas seja o assunto. A regra do `null` é a mesma de sempre
       — some da página sem tocar em mais nada. */
    calotas:  null as Talvez<{ alt: string }>,
    /* A PRATELEIRA DE DISCO DE FREIO (15/09/2026), a última da lista do README
       das fotos. É a melhor prova de "tem a peça" que a loja tem: os códigos
       HF 18H, HF 22A, HF 23C aparecem legíveis nas caixas. Por isso ela vai
       para a seção "O que temos" da home, ao lado das 11 linhas — e não para a
       /sobre, onde disputaria tamanho com a fachada. */
    prateleiras: { alt: 'Prateleira de discos de freio na Serginho Autopeças, com as caixas identificadas por código' } as Talvez<{ alt: string }>,
    vonixx:   { alt: 'Prateleira de produtos de limpeza automotiva Vonixx na Serginho Autopeças' } as Talvez<{ alt: string }>,
    /* AS TRÊS DE 15/09/2026, que fecham a lista do README das fotos. O dono
       confirmou por escrito as duas coisas que elas exigiam: a moto é da loja,
       e a equipe autorizou aparecer.

       `equipe` é a que mais trabalha das três — rosto de gente é o que separa
       uma loja de rua de um marketplace, e o site não tinha nenhuma. A placa
       da moto ficou FORA DO QUADRO, por enquadramento e não por borrão: dado
       que não acrescenta nada a quem lê e fica indexado para sempre. */
    equipe:   { alt: 'Equipe da Serginho Autopeças, cinco pessoas de uniforme, na porta da loja na Rua Cristóvão Colombo, 427' } as Talvez<{ alt: string }>,
    balcao:   { alt: 'Balcão da Serginho Autopeças, com vitrines de acessórios, calotas empilhadas no chão e prateleiras de peças ao fundo' } as Talvez<{ alt: string }>,
    entrega:  { alt: 'Moto de entrega da Serginho Autopeças, com baú, estacionada na porta da loja' } as Talvez<{ alt: string }>,
    /* O MASCOTE (11/09/2026): o avatar 3D do Serginho, de polo da loja e
       joinha — o dono, que é o primeiro cartão da seção "29 anos" em pessoa.
       Recorte com fundo transparente, feito pelo dono. Vive na home; ao passar
       o mouse aparece o balão "Fala bixão", e o clique abre o WhatsApp. */
    mascote:  { alt: 'Serginho, dono da loja, de camisa polo com o logo, fazendo sinal de positivo' } as Talvez<{ alt: string }>,
  },

  /* A EQUIPE COM NOME E ROSTO (15/09/2026). Cinco retratos de estúdio, de polo
     da loja, tirados pelo dono — o material mais profissional que o site tem.

     O NOME É O QUE FAZ A FOTO TRABALHAR. Rosto sem nome é ilustração; rosto com
     nome é a pessoa que atendeu você no balcão semana passada e que você procura
     quando volta. Numa loja de bairro isso vale mais que qualquer adjetivo na
     página, e é o que nenhum marketplace consegue copiar.

     A ORDEM É A DA CASA: o fundador primeiro. Não é hierarquia de organograma —
     é a ordem em que a história da loja se conta, e a /sobre inteira já começa
     em 1997 com ele.

     CARGO NÃO ENTRA sem o dono confirmar cada um. Cargo envelhece (gente muda de
     função, gente sai) e cargo errado publicado é pior que cargo ausente. Nome e
     rosto bastam para o que a seção precisa fazer. */
  equipe: [
    { arquivo: 'serginho',    nome: 'Serginho',       alt: 'Serginho, fundador da Serginho Autopeças, de polo preta com o símbolo S da loja' },
    { arquivo: 'luiz-felipe', nome: 'Luiz Felipe',    alt: 'Luiz Felipe Garcia, da Serginho Autopeças, de polo preta com o símbolo S da loja' },
    { arquivo: 'diego',       nome: 'Diego',          alt: 'Diego dos Santos, da Serginho Autopeças, de polo preta com o símbolo S da loja' },
    { arquivo: 'alexander',   nome: 'Alexander',      alt: 'Alexander Monteiro, da Serginho Autopeças, de polo preta com o símbolo S da loja' },
    { arquivo: 'renan',       nome: 'Renan',          alt: 'Renan Constela, da Serginho Autopeças, de polo preta com o símbolo S da loja' },
  ] as Array<{ arquivo: string; nome: string; alt: string }>,

  // --- 6. Vitrine ------------------------------------------------------------
  /* OS QUATRO PREÇOS SÃO DE BALCÃO, confirmados pelo dono em 14/09/2026 — e
     vieram por uma exigência do Google, não por vontade de anunciar preço: o
     Search Console acusou os 4 itens da vitrine como inválidos ("É preciso
     especificar price ou priceSpecification.price"), porque um Product com
     Offer sem preço não é um produto à venda aos olhos dele.

     A REGRA QUE VEM JUNTO: o preço do dado estruturado tem de estar VISÍVEL na
     página — o Google trata divergência entre os dois como engano. Por isso o
     card mostra o valor e a vitrine leva a nota "Preço de balcão. Sujeito a
     alteração."; as duas coisas andam juntas, nunca só uma.

     `preco: null` continua sendo o certo para item sem preço confirmado: o
     card cai para "Consultar preço" e o Offer sai sem price. Preço
     desatualizado no ar é problema de CDC, então este número precisa ser
     revisto quando a tabela mudar. */
  vitrine: [
    {
      nome: 'Lâmpada Pingo (T10/W5W)',
      marca: 'Bosch',
      preco: 5,
      descricao: 'Lâmpada pingo ideal para luz de lanterna, placa ou iluminação interna com alta durabilidade.',
      foto: { src: '/produtos/lampada-pingo.png', alt: 'Lâmpada pingo T10/W5W de vidro, com filamento à vista' },
    },
    {
      nome: 'Jogo de Calotas Esportivas Aro 14 Silver Grid (4 unidades)',
      marca: 'Grid Autos',
      preco: 110,
      descricao: 'Fabricadas em plástico ABS de alta resistência com acabamento prateado para renovar o visual das rodas.',
      foto: { src: '/produtos/calotas-aro14.png', alt: 'Jogo de quatro calotas aro 14 prateadas, empilhadas em leque' },
    },
    {
      nome: 'Óleo de Motor 5W30 Vegas Synthetic (1 litro)',
      marca: 'Vegas',
      preco: 42,
      descricao: 'Formulação sintética de alta performance que garante proteção contra o desgaste e excelente fluidez na partida a frio.',
      // A primeira foto enviada era de um Vegas 20W50 MINERAL de MOTOCICLETA,
      // e este item é o 5W30 SINTÉTICO de carro — mostrar uma peça e vender
      // outra. Ficou sem foto até o dono mandar a certa, no mesmo dia.
      foto: { src: '/produtos/oleo-vegas.png', alt: 'Frasco de 1 litro do óleo Vegas Life 5W30 API SN, sintético' },
    },
    {
      nome: 'Aditivo para Radiador JN Flex (1 litro)',
      marca: 'JN Automotive',
      preco: 10,
      descricao: 'Protege o sistema de arrefecimento contra corrosão e cavitação, auxiliando no controle da temperatura e vida útil do motor.',
      foto: { src: '/produtos/aditivo-jn-flex.png', alt: 'Frasco de 1 litro do aditivo JN Flex, líquido rosa' },
    },
  ] as Produto[],

  // --- 7. Infra --------------------------------------------------------------
  /** O domínio como se ESCREVE, para gente ler — cartão, fachada, a imagem de
      compartilhamento. Canônico COM cedilha por decisão do dono (2026-08-06).
      Para montar URL, use `siteUrl` abaixo, nunca este valor: na rede o nome é
      o punycode, e as duas formas misturadas dividem a entidade no Google. */
  dominio: 'serginhoautopeças.com.br',
  /** Subdomínio do sistema interno (Workstream B) — não é link público. */
  sistemaUrl: 'https://sistema.serginhoautopeças.com.br',

  /** Container do Google Tag Manager, criado na conta Google da loja em
      2026-09-11. NÃO é segredo: vai no HTML de toda página, é assim que o GTM
      funciona. O GA4 (G-RF210SBGGP) NÃO entra no site — quem o carrega é o
      GTM, e ter as duas coisas no HTML duplicaria cada pageview.

      Desde 06/10/2026 o GTM carrega em toda visita, com o Consent Mode v2 em
      tudo negado: mede sem cookie e sem identificador, e só grava cookie com
      "Aceitar". Quem escolhe "Não medir minhas visitas" (ou navega com o sinal
      de privacidade do navegador, GPC) não carrega nada. Ver Base.astro,
      ConsentBanner.astro e a §2 da política. */
  gtmId: 'GTM-WJ6FVTPH' as Talvez<string>,
  /** O ID do GA4, só para o DESLIGAR: `window['ga-disable-<id>'] = true` é a
      chave oficial do Google para a tag não enviar nada. Não carrega o GA4 —
      quem carrega continua sendo o GTM (ver o comentário de `gtmId`). */
  ga4Id: 'G-RF210SBGGP' as Talvez<string>,
} as const;

/**
 * A URL do site como ela existe NA REDE — punycode, sempre.
 *
 * O `new URL()` não é enfeite: ele é a conversão. `negocio.dominio` tem cedilha,
 * e DNS não transporta acento — o nome na rede é xn--serginhoautopeas-ppb.com.br.
 * Interpolar o domínio direto numa string, como esta linha fazia até 11/09/2026,
 * produzia URL com "ç" em tudo que NÓS montamos à mão.
 *
 * Passava despercebido porque o Astro normaliza sozinho o que passa pelo parser
 * dele: `canonical`, `og:url` e o sitemap já saíam em punycode. Quem não passa
 * por lá saía com o ç — o JSON-LD (`@id` do #loja e do #site) e a linha Sitemap
 * do robots.txt. E o `@id` é justamente o identificador que diz ao Google "esta
 * página e aquela falam do MESMO negócio": com `@id` numa grafia e `url`/
 * `canonical` noutra, o Google via duas entidades onde existe uma loja só.
 *
 * Uma conversão, uma fonte. Mudou o domínio, muda em `negocio.dominio` e aqui
 * sai certo.
 */
export const siteUrl = new URL(`https://${negocio.dominio}`).origin;

// --- Pendências ---------------------------------------------------------------
// `obrigatorio: true` bloqueia o build de produção e mantém o noindex.
// `false` = o site vive sem, mas a seção correspondente não é renderizada.

export interface Pendencia {
  campo: string;
  label: string;
  obrigatorio: boolean;
  onde: string;
}

const vazio = (v: unknown): boolean =>
  v === null || v === undefined || (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'string' && v.trim() === '');

/** Cada linha: [campo, label, obrigatório, seção do site-conteudo.md, está vazio?] */
type Regra = [string, string, boolean, string, boolean];

export function pendencias(): Pendencia[] {
  const n = negocio;
  const regras: Regra[] = [
    ['whatsapp',        'WhatsApp comercial (E.164, DDD 53)',    true,  '§2', vazio(n.whatsapp)],
    ['endereco.rua',    'Rua do estabelecimento',                true,  '§3', vazio(n.endereco.rua)],
    ['endereco.numero', 'Número',                                true,  '§3', vazio(n.endereco.numero)],
    ['endereco.bairro', 'Bairro',                                true,  '§3', vazio(n.endereco.bairro)],
    ['endereco.cep',    'CEP',                                   true,  '§3', vazio(n.endereco.cep)],
    ['horarios',        'Horários de funcionamento',             true,  '§3', vazio(n.horarios)],
    ['linhas',          'Posicionamento carro/moto/ambos (D2)',  true,  '§4', vazio(n.linhas)],
    ['categorias',      'Categorias de peças trabalhadas',       true,  '§4', vazio(n.categorias)],
    ['pagamentos',      'Formas de pagamento aceitas',           true,  '§4', vazio(n.pagamentos)],
    ['cnpj',            'CNPJ (rodapé + páginas legais)',        true,  '§1', vazio(n.cnpj)],
    ['razaoSocial',     'Razão social (páginas legais)',         true,  '§1', vazio(n.razaoSocial)],
    ['anoFundacao',     'Ano exato de fundação (JSON-LD)',       false, '§1', vazio(n.anoFundacao)],
    ['geo',             'Latitude/longitude (JSON-LD)',          false, '§3', vazio(n.geo.lat) || vazio(n.geo.lng)],
    ['mapsUrl',         'Link do Google Maps',                   false, '§3', vazio(n.mapsUrl)],
    ['marcas',          'Marcas trabalhadas',                    false, '§4', vazio(n.marcas)],
    ['diferenciais',    'Diferenciais reais',                    false, '§4', vazio(n.diferenciais)],
    ['entrega',         'Entrega local (bairros/prazo)',         false, '§4', vazio(n.entrega)],
    ['historia',        'História para /sobre',                  false, '§5', vazio(n.historia)],
    // AS FOTOS DA /sobre NÃO ESTÃO MAIS AQUI. As reais da loja chegaram em
    // 11/09/2026 e a página está completa com elas; a foto de ARQUIVO (1997)
    // saiu desta lista em 14/09/2026 por decisão do dono — as fotos antigas
    // da história não entram nesta rodada. Não é dado faltando, é escopo, e
    // pendência que ninguém vai resolver polui o log de todo build até virar
    // ruído que ninguém lê. O slot segue de pé em `fotos.arquivo1997`: no dia
    // em que a foto aparecer, o caminho e o alt ali fazem o capítulo 01 voltar
    // sozinho a duas colunas — e esta linha pode voltar junto.
    ['vitrine',         'Produtos reais da vitrine',             false, '§6', vazio(n.vitrine)],
    ['instagram',       'Instagram (sameAs)',                    false, '§2', vazio(n.instagram)],
  ];

  return regras
    .filter(([, , , , estaVazio]) => estaVazio)
    .map(([campo, label, obrigatorio, secao]) => ({
      campo, label, obrigatorio, onde: `data/site-conteudo.md ${secao}`,
    }));
}

/** Bloqueia produção e liga noindex. */
export const conteudoIncompleto = (): boolean =>
  pendencias().some((p) => p.obrigatorio);

// --- Derivados ----------------------------------------------------------------

export const enderecoLinha = (): Talvez<string> => {
  const e = negocio.endereco;
  if (!e.rua || !e.numero) return null;
  return `${e.rua}, ${e.numero}${e.bairro ? ` — ${e.bairro}` : ''}`;
};

/**
 * O endereço COMPLETO numa frase — rua, número, bairro, cidade/UF e CEP.
 *
 * `enderecoLinha()` para na rua e no bairro, porque é o que o rodapé mostra na
 * primeira linha (o CEP e a cidade vêm na segunda). Para o FAQ isso não serve:
 * a dica daquele item pede o NAP inteiro, igual ao do rodapé e ao do Google
 * Business Profile — e NAP divergente derruba SEO local. Sem esta função, a
 * resposta do FAQ perderia cidade e CEP ao ser gerada do dado (11/09/2026).
 */
export const enderecoCompleto = (): Talvez<string> => {
  const base = enderecoLinha();
  if (!base) return null;
  const cidade = [negocio.cidade, negocio.uf].filter(Boolean).join('/');
  const cep = negocio.endereco.cep ? `CEP ${negocio.endereco.cep}` : '';
  return [base, cidade, cep].filter(Boolean).join(', ');
};

/** '+555399998888' -> '(53) 9999-8888' */
export const telefoneFormatado = (e164: Talvez<string>): Talvez<string> => {
  if (!e164) return null;
  const d = e164.replace(/\D/g, '').replace(/^55/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return e164;
};

/**
 * A marca de origem do site (plano do time de agentes v2, §4.7, item 1.6a,
 * 25/10/2026): toda conversa que o site abre no WhatsApp começa dizendo de
 * onde veio, na frase do dono — "Vim pelo site". A coleta das 6h do Estúdio lê
 * a primeira mensagem de cada conversa nova e conta a que traz "vim pelo site"
 * (ou "vi no site", como no card da vitrine) no canal "site" da métrica-mãe,
 * ao lado da bio do Instagram ("Vi no Instagram"), do link do Perfil do Google
 * ("Vi no Google") e do da Página do Facebook ("Vi no Facebook"). Quem garante
 * é o `whatsappLink`: mensagem sem a marca ganha "Vim pelo site. " na frente
 * (no começo, porque várias terminam em espaço para o cliente completar com o
 * carro dele).
 */
export const MARCA_ORIGEM_SITE = 'Vim pelo site';
const TEM_MARCA_DO_SITE = /\b(?:vi no|vim pel[oa]) site\b/i;

/** Mensagem padrão de todos os CTAs de WhatsApp do site (definida pelo dono). */
export const WHATSAPP_MENSAGEM_PADRAO =
  'Vim pelo site e gostaria de realizar um orçamento';

/**
 * A mensagem MUDA CONFORME O BOTÃO, e isso trabalha para quem atende.
 *
 * Até 11/09/2026 todo link do site abria o WhatsApp com a mesma frase. Quem
 * atende recebia "Vim pelo site e gostaria de realizar um orçamento" e tinha de
 * perguntar tudo do zero, inclusive para quem tinha acabado de clicar no card
 * de uma peça específica — a informação existia na tela e se perdia no caminho.
 *
 * Note que a do card já vem com o nome da peça: a conversa começa com a loja
 * sabendo o que o cliente quer.
 */
export const MSG_WA = {
  consulta: 'Olá! Vim pelo site e quero consultar uma peça.',
  /** O mascote responde no tom dele: quem clica no "Fala bixão" abre a
      conversa já dizendo "Fala bixão". */
  mascote: 'Fala bixão! Vim pelo site e quero consultar uma peça.',
  rodape: 'Olá! Vim pelo site (rodapé) e quero consultar uma peça.',
  produto: (nome: string) => `Olá! Vi no site: ${nome}. Tem disponível?`,
} as const;

/**
 * Rota no Waze até a loja, montada das coordenadas — derivado, não digitado
 * (regra 4). Entra no `sameAs` do cartão da loja: é um dos lugares em que a
 * loja "existe" na rede, como o Maps, e o Google usa esses links para ter
 * certeza de que o perfil no mapa e o site são o mesmo negócio.
 */
export const wazeUrl = (): Talvez<string> => {
  if (negocio.geo.lat === null || negocio.geo.lng === null) return null;
  return `https://www.waze.com/pt-BR/live-map/directions?to=ll.${negocio.geo.lat}%2C${negocio.geo.lng}`;
};

/** O wa.me SEM mensagem — para dado estruturado, onde texto pré-escrito não cabe. */
export const whatsappUrl = (): Talvez<string> => {
  if (!negocio.whatsapp) return null;
  return `https://wa.me/${negocio.whatsapp.replace(/\D/g, '')}`;
};

/** A mensagem que o site manda ao WhatsApp, sempre com a marca de origem:
    a que já diz "Vim pelo site" ou "Vi no site" fica como está; a outra ganha
    "Vim pelo site. " na frente (ver MARCA_ORIGEM_SITE). */
export const comMarcaDoSite = (mensagem: string): string =>
  TEM_MARCA_DO_SITE.test(mensagem) ? mensagem : `${MARCA_ORIGEM_SITE}. ${mensagem}`;

/** Link wa.me com mensagem pré-preenchida — e com a marca de origem do site
    (comMarcaDoSite). null quando o número não existe. */
export const whatsappLink = (
  mensagem: string = WHATSAPP_MENSAGEM_PADRAO,
): Talvez<string> => {
  if (!negocio.whatsapp) return null;
  return `https://wa.me/${negocio.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(comMarcaDoSite(mensagem))}`;
};

const DIAS_PT = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const DIAS_SCHEMA = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const horarioLegivel = (h: Horario): string => {
  const ordenados = [...h.dias].sort((a, b) => a - b);
  // Índices defendidos: desde 17/08/2026 este arquivo também é lido pelo APP
  // (o orçamento impresso precisa de CNPJ e endereço), e lá o TypeScript roda
  // com noUncheckedIndexedAccess — acesso a array devolve `| undefined`.
  // Continua sendo a fonte única do dado de negócio; só ficou mais defensivo.
  const nome = (d: number | undefined) => (d === undefined ? '' : DIAS_PT[d] ?? '');
  const seq = ordenados.every((d, i) => {
    const anterior = ordenados[i - 1];
    return i === 0 || anterior === undefined || d === anterior + 1;
  });
  const faixa = ordenados.length > 1 && seq
    ? `${nome(ordenados[0])} a ${nome(ordenados[ordenados.length - 1])}`
    : ordenados.map(nome).join(', ');
  return `${faixa}: ${h.abre} às ${h.fecha}`;
};

/**
 * Os horários numa frase corrida, do jeito que se fala — para o FAQ e para
 * qualquer texto que precise dizer quando a loja abre.
 *
 * POR QUE EXISTE (11/09/2026): a resposta do FAQ trazia o horário DIGITADO à
 * mão ("aos sábados das 8h30 às 12h"). Quando o sábado passou a fechar 12:15, o
 * dado mudou em `horarios` e a frase do FAQ continuou dizendo 12h — no mesmo
 * item cuja dica avisa que divergência de horário derruba o SEO local. Horário
 * em dois lugares diverge no primeiro dia em que muda; agora é um só.
 *
 * Formato falado (8h30, 12h15) e não o técnico (08:30): é resposta de FAQ, lida
 * por gente. O JSON-LD continua usando os campos crus.
 */
export const horariosEmFrase = (hs: Horario[] = negocio.horarios): string => {
  if (hs.length === 0) return '';
  const falado = (hhmm: string) => {
    const [h, m] = hhmm.split(':');
    const hora = String(Number(h));
    return m && m !== '00' ? `${hora}h${m}` : `${hora}h`;
  };
  // Agrupa as faixas do MESMO conjunto de dias: seg-sex tem duas (almoço).
  const porDias = new Map<string, Horario[]>();
  for (const h of hs) {
    const chave = [...h.dias].sort((a, b) => a - b).join(',');
    porDias.set(chave, [...(porDias.get(chave) ?? []), h]);
  }
  const partes = [...porDias.values()].map((faixas) => {
    const primeira = faixas[0];
    if (!primeira) return '';
    const rotulo = horarioLegivel(primeira).split(':')[0] ?? '';
    const janelas = faixas.map((f) => `das ${falado(f.abre)} às ${falado(f.fecha)}`);
    const texto = janelas.length > 1
      ? `${janelas.slice(0, -1).join(', ')} e ${janelas[janelas.length - 1]}`
      : janelas[0];
    return `${rotulo.toLowerCase()}, ${texto}`;
  }).filter(Boolean);
  return partes.join('; ');
};

export const diasSchema = (h: Horario): string[] =>
  h.dias.map((d) => DIAS_SCHEMA[d]).filter((d): d is string => d !== undefined);

/** Texto de linha de produto conforme D2 — nunca afirma antes de confirmar. */
export const linhasTexto = (): Talvez<string> => {
  switch (negocio.linhas) {
    case 'carro': return 'carros';
    case 'moto':  return 'motos';
    case 'ambos': return 'carros e motos';
    default:      return null;
  }
};
